import { useContext, useCallback } from 'react';
import Promise from 'bluebird';
import { extend, get } from 'underscore';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { publisherQueries } from '@bingads-webui-campaign-react/merchant-shared';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { readCampaign, readAssetGroup } from '@bingads-webui-campaign-react/perf-max-data';
import {
  getBundleCountriesAndBundleLocationCriterions,
} from '@bingads-webui-campaign/hotel-common-targets';

import {
  saveAssetGroupAndLinkedData,
  modelToViewModelForAssetGroup,
} from '../../model/asset-group';
// getErrorsPlaceholders,
// loadAssetGroup,
// viewModelToModelForAssetGroup,

import { modelToViewModelForCampaign } from '../../model/campaign';
// loadCampaign, viewModelToModelForCampaign

import { getDefaultCampaign, getDefaultAssetGroup } from './constants';

import { getMSStoreIdByEnv } from './utils';
import { saveCampaign } from '../../model/campaign-bulk';
import { getProductData, getProductScopeFromOData } from './getProductScope';

export const useAssetGroupRepository = () => {
  const { send, state } = useWizardService();
  const i18n = useContext(I18nContext);
  const { scope } = useContext(ScopeContext) || {};
  const {
    cid,
    aid,
    uid,
    campaignId: cmpId,
    assetGroupId: agId,
  } = scope.values;
  const scenarioContext = useContext(ScenarioContext);
  const activityContext = useContext(ActivityContext);
  const serverContext = useContext(ServerContext);
  const acid = serverContext.CurrentAccount.AdvertiserCustomerID;

  const load = () => {
    const campaignId = cmpId || (state.context.wizardData.campaign && state.context.wizardData.campaign.Id);
    const assetGroupId = agId || (state.context.wizardData.assetGroup && state.context.wizardData.assetGroup.Id);
    const lcid = serverContext ? serverContext.CurrentUser.LocaleID : '1033';
    const isOdataGetProductScopeForAppAdsEnabled = get(serverContext, ['DynamicPermissions', 'OdataGetProductScopeForAppAds'], false);

    const campaignPromise = campaignId ? readCampaign(cid, aid, campaignId) : Promise.resolve(null);
    const assetGroupPromise = (campaignId && assetGroupId) ? readAssetGroup(cid, aid, campaignId, assetGroupId) : Promise.resolve(null);

    let loadPromises = {
      campaign: campaignPromise,
      bundleCountriesAndLocationCriterions: campaignId ? Promise.resolve(null) : getBundleCountriesAndBundleLocationCriterions(cid, aid, lcid, 'English', 'US'),
      assetGroup: assetGroupPromise,
    };
    let callStartTime = null;

    if (campaignId) {
      callStartTime = Date.now();
      loadPromises = extend(loadPromises, {
        productScope: isOdataGetProductScopeForAppAdsEnabled ?
          getProductScopeFromOData(campaignId, serverContext) :
          getProductData({
            cid,
            aid,
            uid,
            campaignId,
          }),
      });
    }

    return Promise.props(loadPromises).then(({
      campaign,
      assetGroup,
      productScope,
      bundleCountriesAndLocationCriterions,
    }) => {
      const campaignVM = campaign ? modelToViewModelForCampaign({ campaignModel: campaign }) : { data: null }; // initial campaign data?
      const assetGroupVMPromise = assetGroup ? modelToViewModelForAssetGroup(assetGroup, i18n, cid, aid) : Promise.resolve({ data: getDefaultAssetGroup(i18n).data });
      const loadAppPromise = () => {
        if (campaignVM.data) {
          if (callStartTime !== null && activityContext) {
            if (isOdataGetProductScopeForAppAdsEnabled) {
              activityContext.signal(`[AppAds][OdataGetProductScopeForAppAds] GetProductDataCallTime: ${Date.now() - callStartTime}`);
            } else {
              activityContext.signal(`[AppAds] GetProductDataCallTime: ${Date.now() - callStartTime}`);
            }
          }

          const appId = productScope.CampaignCriterionsData.Criterion.ProductConditions[0].Attribute;
          return publisherQueries.getMatchedPublishers(acid, appId, 'Application')
            .then((response) => {
              if (response && response.MatchingAppications && response.MatchingAppications.length > 0) {
                const apps = response.MatchingAppications;
                const app = apps.find(item => item.AppId === appId);
                if (app) {
                  app.isPreselected = true;
                  return Promise.resolve(app);
                }
              }
              return Promise.resolve();
            });
        } // TO DO: error handling on app loading
        return Promise.resolve();
      };

      return loadAppPromise().then(app => assetGroupVMPromise.then(assetGroupVM => ({
        campaign: {
          StoreApp: app,
          ...campaignVM.data,
        },
        assetGroup: assetGroupVM.data,
        ...bundleCountriesAndLocationCriterions,
      })));
    });
  };

  // TO DO: error handling
  const createPublishAppPromise = useCallback(({ campaign: { StoreApp } }) => publisherQueries.createPublisher(acid, StoreApp.SellerId), [acid]);

  // TO DO: error handling
  const createGetStoreIdPromise = useCallback(publishAppPromise => publishAppPromise
    .then(() => getMSStoreIdByEnv())
    .catch((response) => {
      if (response.raw && response.raw.Message === 'Seller Id already exist') {
        return getMSStoreIdByEnv();
      }
      return null;
    }), []);

  const createSaveCampaignPromise = useCallback((getStoreIdPromise, appId, bundleLocationCriterions) => getStoreIdPromise.then((storeId) => {
    if (state.context.wizardData.campaign) {
      return new Promise((resolve) => {
        resolve({ campaign: { data: state.context.wizardData.campaign } });
      });
    }

    return saveCampaign({
      campaign: getDefaultCampaign(i18n, serverContext),
      storeId,
      appId,
      bundleLocationCriterions,
    }, {
      scenario: scenarioContext.name,
      activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
      i18n,
    });
  }), [i18n, scenarioContext, serverContext, state.context.wizardData.campaign]);

  const createAssetGroup = useCallback(
    (saveCampaignPromise, data) => Promise.all([saveCampaignPromise])
      .then(([campaignData]) => {
        const campaignMtModel = campaignData.campaign.data;
        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: campaignData.campaign.data });

        if (activityContext) {
          activityContext.signal(`campaignId - ${campaignMtModel.Id}`);
        }

        return saveAssetGroupAndLinkedData({
          assetGroup: {
            data: {
              ...data.assetGroup, // existing data from model
            },
          },
          originalAssetGroup: {
            data: data.assetGroup,
          }, // existing model data
          campaign: campaignMtModel,
          audiencesSignal: data.audiencesSignal,
        }, {
          scenario: scenarioContext.name,
          i18n,
          activityLogger: scenarioContext.createActivity('saveDefaultAssetGroup'),
          serverContext,
          cid,
          aid,
        });
      })
      // eslint-disable-next-line prefer-promise-reject-errors
      .catch(error => Promise.reject({ error: { errors: error.serverErrors } })),
    [send, activityContext, aid, cid, i18n, scenarioContext, serverContext]
  );

  const save = ({ data }) => {
    let saveCampaignPromise = null;
    if (data.campaign && data.campaign.Id) {
      saveCampaignPromise = new Promise((resolve) => {
        resolve({
          campaign: {
            data: data.campaign,
          },
        });
      });
    } else {
      saveCampaignPromise = createSaveCampaignPromise(createGetStoreIdPromise(createPublishAppPromise(data)), data.campaign.StoreApp.AppId, data.bundleLocationCriterions);
    }
    return createAssetGroup(saveCampaignPromise, data).then((assetGroupData) => {
      send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { assetGroup: assetGroupData.assetGroup.data });
      return Promise.resolve(assetGroupData);
    });
  };

  return {
    load,
    save,
  };
};
